// EMİR #68a (Kurul-F oybirliği) — kutu-açılış SES + TİTREŞİM katmanı. UI-only.
//
// KURALLAR (F4 hükmü, bağlayıcı):
// - Ses YALNIZ kullanıcı jestiyle başlar: AudioContext, kutuya dokunma anında
//   primeRevealAudio() ile (jest bağlamında) yaratılır/resume edilir; sonrası
//   timer-güdümlü çağrılar bu hazır context'i kullanır. Jest yoksa SES YOK.
// - Dosya yok: tüm sesler WebAudio SENTEZ (0KB indirme; ≤50KB kuralının altında).
// - Kapatılabilir: localStorage 'mylasa.mycards.sound' ("0"=kapalı, default açık);
//   modal başlığında 🔊/🔇 toggle. reduced-motion'da otomatik SESSİZ.
// - Titreşim merdiveni (Kurul-F): common tek küt · rare çift · legendary güçlü
//   çift + kuyruk · 1/1 kalp atışı. navigator.vibrate yoksa sessiz no-op.
// Ekonomi/mint/kart verisine DOKUNMAZ.

import { useEffect, useRef, useState } from "react";
import { prefersReducedMotion } from "./cardMeta";
import type { RevealStage, RevealTier } from "./cardMeta";

const SOUND_KEY = "mylasa.mycards.sound";

export function revealSoundEnabled(): boolean {
  try { return window.localStorage.getItem(SOUND_KEY) !== "0"; } catch { return true; }
}
export function setRevealSoundEnabled(on: boolean): void {
  try { window.localStorage.setItem(SOUND_KEY, on ? "1" : "0"); } catch { /* no-op */ }
}

let _ctx: AudioContext | null = null;

// EMİR #80-B.8 — GERÇEK SFX katmanı: public/sfx/mycards/*.wav (pre-render katmanlı
// one-shot'lar; toplam ≤150KB). Yükleme jest sonrası LAZY (primeRevealAudio →
// kickSfxLoad). Dosya yok/decode hatası → o ses için SESSİZCE mevcut sentetik
// fallback çalışır (null işareti). Owner lisanslı bir seti (Kenney vb.) aynı
// dosya adlarıyla public/sfx/mycards/'a koyarsa otomatik onlar çalar.
const SFX_FILES: Record<string, string> = {
  sealCrack: "/sfx/mycards/seal-crack.wav",
  boxImpact: "/sfx/mycards/box-impact.wav",
  cardSwish: "/sfx/mycards/card-swish.wav",
  legendaryChime: "/sfx/mycards/legendary-chime.wav",
};
// undefined = henüz yüklenmedi · null = hata (sentetik fallback) · AudioBuffer = hazır
const _sfxBuf: Record<string, AudioBuffer | null | undefined> = {};
let _sfxKicked = false;
function kickSfxLoad(): void {
  if (_sfxKicked || !_ctx) return;
  _sfxKicked = true;
  const c = _ctx; // döngü kapanışlarına sabit referans (no-loop-func)
  for (const [key, url] of Object.entries(SFX_FILES)) {
    fetch(url)
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error("http " + r.status))))
      .then((ab) => c.decodeAudioData(ab))
      .then((buf) => { _sfxBuf[key] = buf; })
      .catch(() => { _sfxBuf[key] = null; }); // SESSİZ fallback → sentetik
  }
}
/** Sample çal (varsa). true = çaldı; false = buffer yok → çağıran sentetik çalar. */
function playSample(c: AudioContext, key: string, t0: number, peak = 0.5): boolean {
  const buf = _sfxBuf[key];
  if (!buf) return false;
  try {
    const src = c.createBufferSource();
    src.buffer = buf;
    const g = c.createGain();
    g.gain.value = peak;
    src.connect(g).connect(c.destination);
    src.start(t0);
    return true;
  } catch { return false; }
}

/** Kullanıcı JESTİ içinde çağrılır (kutu dokunuşu): context yarat + resume.
 *  Autoplay policy bu jestle tatmin olur; sonraki timer-sesler çalabilir. */
export function primeRevealAudio(): void {
  if (!revealSoundEnabled() || prefersReducedMotion()) return;
  try {
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    if (!_ctx) _ctx = new AC();
    if (_ctx && _ctx.state === "suspended") void _ctx.resume();
    kickSfxLoad(); // #80-B.8 — jest geldi → sample'ları arka planda indir/decode et
  } catch { /* ses hiçbir akışı KIRMAZ */ }
}

function ctx(): AudioContext | null {
  if (!revealSoundEnabled() || prefersReducedMotion()) return null;
  return _ctx && _ctx.state === "running" ? _ctx : null;
}

// Küçük yardımcılar — hepsi kısa zarflı, düşük gain (≤0.22), kendi kendini durdurur.
function tone(
  c: AudioContext, freq: number, t0: number, dur: number,
  type: "sine" | "triangle" | "square" | "sawtooth" = "sine", peak = 0.15, sweepTo?: number,
): void {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t0);
  if (sweepTo) o.frequency.exponentialRampToValueAtTime(Math.max(30, sweepTo), t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + Math.min(0.03, dur / 4));
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g).connect(c.destination);
  o.start(t0); o.stop(t0 + dur + 0.02);
}
function noiseBurst(c: AudioContext, t0: number, dur: number, peak = 0.18, lpFreq = 2400): void {
  const n = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, Math.max(1, n), c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
  const src = c.createBufferSource();
  src.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = "lowpass"; f.frequency.value = lpFreq;
  const g = c.createGain();
  g.gain.setValueAtTime(peak, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(f).connect(g).connect(c.destination);
  src.start(t0);
}

/** Mühür çatlaması (charge girişi): GERÇEK sample (seal-crack) varsa o; yoksa
 *  sentetik "çıt" fallback (#80-B.8). Rare+ yükselen bekleyiş tonu her iki yolda. */
export function fxSealCrack(tier: RevealTier): void {
  const c = ctx(); if (!c) return;
  try {
    const t = c.currentTime;
    if (!playSample(c, "sealCrack", t, 0.55)) {
      noiseBurst(c, t, 0.08, 0.14, 3200);           // çıt (sentetik fallback)
      tone(c, 70, t, 0.5, "triangle", 0.06, 55);    // alçak gerilim
    }
    if (tier !== "common") tone(c, 220, t + 0.15, 0.9, "sine", 0.05, 440); // yükselen bekleyiş
  } catch { /* no-op */ }
}

/** PATLAMA: GERÇEK sample (whoosh+impact) + legendary/1-1'de chime sample;
 *  sample yoksa mevcut sentetik rarity-merdiveni AYNEN (#80-B.8 fallback). */
export function fxBurst(tier: RevealTier): void {
  const c = ctx(); if (!c) return;
  try {
    const t = c.currentTime;
    const sampled = playSample(c, "boxImpact", t, tier === "common" ? 0.42 : 0.52);
    if (!sampled) {
      noiseBurst(c, t, 0.22, tier === "common" ? 0.16 : 0.2, 5200);
      tone(c, 523.25, t, 0.5, "sine", 0.12);                       // C5
      if (tier === "rare" || tier === "legendary" || tier === "oneOfOne") {
        tone(c, 659.25, t + 0.02, 0.6, "sine", 0.1);               // E5
        tone(c, 783.99, t + 0.05, 0.7, "sine", 0.09);              // G5
      }
      if (tier === "legendary" || tier === "oneOfOne") {
        tone(c, 65, t, 0.7, "sine", 0.18, 45);                     // gümleme
        tone(c, 1046.5, t + 0.1, 1.0, "sine", 0.07);               // C6 parıltı
      }
    }
    if (tier === "legendary" || tier === "oneOfOne") {
      // Görkem katmanı: chime sample (impact'in hemen ardından); yoksa sentetik sting.
      if (!playSample(c, "legendaryChime", t + 0.16, tier === "oneOfOne" ? 0.5 : 0.4) && sampled) {
        tone(c, 1046.5, t + 0.1, 1.0, "sine", 0.07);
      }
      if (tier === "oneOfOne" && !sampled) {
        // benzersiz sting (yalnız tam-sentetik yolda; sample yolunda chime taşıyor)
        [1318.5, 1568, 2093].forEach((f, i) => tone(c, f, t + 0.2 + i * 0.12, 1.2, "sine", 0.06));
      }
    }
  } catch { /* no-op */ }
}

/** #80-B.8 — KART GİRİŞİ: kartın havayı kesişi (card-swish sample; yoksa hafif
 *  sentetik süpürme). stage "card"a geçerken bir kez (driver dedup'lu). */
export function fxCardSwish(): void {
  const c = ctx(); if (!c) return;
  try {
    const t = c.currentTime;
    if (!playSample(c, "cardSwish", t, 0.4)) {
      noiseBurst(c, t, 0.16, 0.08, 6000);            // hafif hava kesişi (fallback)
      tone(c, 1800, t, 0.22, "sine", 0.03, 5200);
    }
  } catch { /* no-op */ }
}

/** Koreografi FX sürücüsü: stage geçişlerine ses+titreşim bağlar (faz başına
 *  BİR kez; tier item gelince değişse de çift-tetik yok; replay'de sıfırlanır —
 *  slam'a dönüş dedup'u temizler). Modal'lar tek satırla kullanır. */
export function useRevealFxDriver(isReveal: boolean, stage: RevealStage, tier: RevealTier): void {
  const playedRef = useRef<{ charge: boolean; burst: boolean; swish: boolean }>({ charge: false, burst: false, swish: false });
  useEffect(() => {
    if (!isReveal) return;
    if (stage === "slam") playedRef.current = { charge: false, burst: false, swish: false }; // replay reset
    else if (stage === "charge" && !playedRef.current.charge) {
      playedRef.current.charge = true;
      fxSealCrack(tier);
    } else if (stage === "burst" && !playedRef.current.burst) {
      playedRef.current.burst = true;
      fxBurst(tier);
      vibrateFor(tier);
    } else if (stage === "card" && !playedRef.current.swish) {
      // #80-B.8 — kart uçuş/flip'e girerken swish (yalnız burst YAŞANDIYSA —
      // skip/reduced-motion direkt "card" yolunda jest-öncesi ses çalınmaz).
      playedRef.current.swish = true;
      if (playedRef.current.burst) fxCardSwish();
    }
  }, [isReveal, stage, tier]);
}

/** Ses toggle state'i (🔊/🔇 buton için): localStorage-kalıcı. */
export function useRevealSoundToggle(): { soundOn: boolean; toggleSound: () => void } {
  const [soundOn, setSoundOn] = useState(() => revealSoundEnabled());
  const toggleSound = () => {
    setSoundOn((v) => {
      setRevealSoundEnabled(!v);
      if (!v) primeRevealAudio(); // açma da bir jest — context'i hemen hazırla
      return !v;
    });
  };
  return { soundOn, toggleSound };
}

/** Titreşim merdiveni (Kurul-F). Patlama anında çağrılır. */
export function vibrateFor(tier: RevealTier): void {
  if (prefersReducedMotion()) return;
  try {
    const v = (navigator as any).vibrate?.bind(navigator);
    if (!v) return;
    if (tier === "common") v(35);
    else if (tier === "rare") v([45, 70, 45]);
    else if (tier === "legendary") v([70, 60, 70, 60, 160]);
    else v([50, 90, 50, 260, 50, 90, 50]); // 1/1 kalp atışı
  } catch { /* no-op */ }
}
